import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Avatar from '@material-ui/core/Avatar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Skeleton from '@material-ui/lab/Skeleton';
import AppDock from '@afosto/components-react/AppDock';
import AppDockList from '@afosto/components-react/AppDockList';
import AppDockItem from '@afosto/components-react/AppDockItem';
import AppDockFooter from '@afosto/components-react/AppDockFooter';
import AppDockPopper from '@afosto/components-react/AppDockPopper';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useApps from '@afosto/utils-react/subscription/useApps';
import availableApps from '@afosto/utils-react/subscription/apps';
import { FormattedMessage } from 'react-intl';
import { useAppState } from '../AppProvider';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import Icon from '../Icon';
import SubtenantDialog from '../SubtenantDialog';
import gravatarUrl from '@afosto/utils/gravatarUrl';
import { tenants as tenantsApi } from '@afosto/iam/api/Tenants';

const DockList = ({ appKey }) => {
  const apps = useApps();
  const intl = useIntl();

  return (
    <AppDockList>
      {apps.map(({ key, icon, translationKey, label, url }) => (
        <AppDockItem
          key={key}
          icon={<Icon icon={['fal', icon]} size="small" />}
          label={intl.formatMessage({
            id: translationKey,
            defaultMessage: label,
            description: `Label for app: ${label}`,
          })}
          href={url}
          active={appKey === key}
        />
      ))}
    </AppDockList>
  );
};

DockList.defaultProps = {
  appKey: undefined,
};

DockList.propTypes = {
  appKey: PropTypes.string,
};

const DockNotifications = ({ appKey }) => {
  const intl = useIntl();
  const [selectedTab, setSelectedTab] = useState('current');
  const activeApp = availableApps.find(app => app.key === appKey);

  return (
    <AppDockPopper
      icon={<Icon icon={['fal', 'bell']} size="small" />}
      label={intl.formatMessage({
        id: 'app.dock.notifications',
        defaultMessage: 'Notifications',
        description: 'Label of the dock item `Notifications`',
      })}
    >
      <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
        {activeApp && (
          <Tab
            value="current"
            label={intl.formatMessage({
              id: `app.dock.${activeApp.key}`,
              defaultMessage: activeApp.label,
              description: `Label for notification of app: ${activeApp.label}`,
            })}
          />
        )}
        <Tab
          value="all"
          label={intl.formatMessage({
            id: 'app.dock.notificationsMenu.all',
            defaultMessage: 'All notifications',
            description: 'label of the global notifications tab',
          })}
        />
      </Tabs>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20,
        }}
      >
        <Typography variant="caption">
          <FormattedMessage
            {...{
              id: 'app.dock.notificationsMenu.none',
              defaultMessage: 'No notifications',
              description: 'message to show when notifications are empty',
            }}
          />
        </Typography>
      </div>
    </AppDockPopper>
  );
};

DockNotifications.defaultProps = {
  appKey: undefined,
};

DockNotifications.propTypes = {
  appKey: PropTypes.string,
};

const DockUser = () => {
  const intl = useIntl();
  const sessionManager = useSessionManager();
  const { user } = sessionManager ?? {};

  return (
    <>
      <AppDockPopper
        icon={<Avatar src={user && gravatarUrl(user.email, { letterValue: user.name })} />}
        label={intl.formatMessage({
          id: 'app.dock.user',
          defaultMessage: 'User',
          description: 'Label of the dock item `User`',
        })}
      >
        <MenuList>
          <MenuItem onClick={() => sessionManager.signOut()}>
            <FormattedMessage
              {...{
                id: 'app.dock.userMenu.signOut',
                defaultMessage: 'Sign out',
                description: 'label of the sign out link in the user menu',
              }}
            />
          </MenuItem>
        </MenuList>
      </AppDockPopper>
    </>
  );
};

const DockTenant = () => {
  const [tenantInfo, setTenantInfo] = useState({});
  const [isFetchingTenant, setIsFetchingTenant] = useState(true);
  const [showTenantSwitchDialog, setShowTenantSwitchDialog] = useState(false);
  const sessionManager = useSessionManager();
  const { tenant: tokenTenant } = sessionManager?.tokenMetadata ?? {};

  useEffect(() => {
    let isMounted = true;

    const fetchTenant = async () => {
      setIsFetchingTenant(true);
      try {
        const response = await tenantsApi.getTenant(tokenTenant);
        const { data = {} } = response;
        if (isMounted) {
          setTenantInfo(data);
        }
      } catch (error) {
        // do something with the error
      } finally {
        setIsFetchingTenant(false);
      }
    };

    if (tokenTenant) {
      fetchTenant();
    }

    return () => {
      isMounted = false;
    };
  }, [tokenTenant]);

  return (
    <>
      <AppDockItem
        icon={
          isFetchingTenant ? (
            <Skeleton
              variant="circle"
              width={32}
              height={32}
              style={{ backgroundColor: 'rgba(255, 255, 255, .1)' }}
            />
          ) : (
            <Avatar
              src={gravatarUrl('', { letterValue: tenantInfo?.name, size: 35 })}
              style={{ width: '100%', height: '100%' }}
            />
          )
        }
        label={tenantInfo?.name ?? '-'}
        onClick={() => setShowTenantSwitchDialog(true)}
        size="small"
        style={{ marginBottom: '-12px' }}
      />
      <SubtenantDialog
        isOpen={showTenantSwitchDialog}
        onClose={() => setShowTenantSwitchDialog(false)}
      />
    </>
  );
};

const Dock = () => {
  const { appKey } = useAppState();

  return (
    <AppDock>
      <DockList appKey={appKey} />
      <AppDockFooter>
        <AppDockList>
          {/* <DockNotifications appKey={appKey} /> */}
          <DockTenant />
          <DockUser />
        </AppDockList>
      </AppDockFooter>
    </AppDock>
  );
};

export default Dock;
